import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_OPTIONS = [
  { value: 'az', label: 'Azərbaycan dili', flag: '🇦🇿' },
  { value: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'ru', label: 'Русский', flag: '🇷🇺' },
];

export const RestaurantDetail = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    fetchRestaurant();
  }, [restaurantId]);

  const fetchRestaurant = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`, {
        withCredentials: true,
      });
      setRestaurant(data);
    } catch (error) {
      console.error('Restoran yüklənmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveLanguageSettings = async (updates) => {
    setSaving(true);
    setSavedMsg('');
    try {
      const payload = {
        name: restaurant.name,
        description: restaurant.description,
        description_en: restaurant.description_en,
        description_ru: restaurant.description_ru,
        address: restaurant.address,
        phone: restaurant.phone,
        logo_url: restaurant.logo_url,
        cover_url: restaurant.cover_url,
        service_fee_percentage: restaurant.service_fee_percentage,
        currency: restaurant.currency,
        currency_symbol: restaurant.currency_symbol,
        business_type: restaurant.business_type,
        default_language: restaurant.default_language || 'az',
        auto_translate: restaurant.auto_translate ?? true,
        ...updates,
      };
      const { data } = await axios.put(
        `${BACKEND_URL}/api/restaurants/${restaurantId}`,
        payload,
        { withCredentials: true }
      );
      setRestaurant(data);
      setSavedMsg('✅ Tənzimləmələr yadda saxlanıldı');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (err) {
      setSavedMsg('❌ Xəta: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <p className="text-red-600">Restoran tapılmadı</p>
      </div>
    );
  }

  const qrMenuUrl = `${window.location.origin}/qr-menu/${restaurantId}`;
  const currentDefault = restaurant.default_language || 'az';
  const autoOn = restaurant.auto_translate ?? true;

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        <button onClick={() => navigate('/dashboard')} className="text-emerald-600 hover:underline">
          ← Geri
        </button>

        {/* Restaurant info card */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
          <div className="flex items-start space-x-6 mb-6">
            {restaurant.logo_url && (
              <img
                src={restaurant.logo_url}
                alt={restaurant.name}
                className="w-28 h-28 object-cover rounded-xl"
              />
            )}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-stone-900 mb-2" data-testid="restaurant-detail-name">
                {restaurant.name}
              </h1>
              {restaurant.description && <p className="text-stone-600 mb-4">{restaurant.description}</p>}
              <div className="space-y-1 text-sm text-stone-700">
                <p>📍 <strong>Ünvan:</strong> {restaurant.address || '—'}</p>
                <p>📞 <strong>Telefon:</strong> {restaurant.phone}</p>
                <p>💰 <strong>Valyuta:</strong> {restaurant.currency}</p>
              </div>
            </div>
          </div>
        </div>

        {/* LANGUAGE SETTINGS CARD */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8" data-testid="language-settings">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-stone-900">🌐 Dil tənzimləmələri</h2>
              <p className="text-sm text-stone-500 mt-1">
                Menyu yazdığınız əsas dili seçin. Sistem digər dillərə avtomatik tərcümə edəcək.
              </p>
            </div>
            {savedMsg && <span className="text-sm font-medium" data-testid="settings-saved-msg">{savedMsg}</span>}
          </div>

          {/* Default language selector */}
          <div className="mt-5">
            <label className="block text-sm font-semibold text-stone-700 mb-3">
              Menyunun əsas dili (siz bu dildə yazırsınız)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {LANG_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => saveLanguageSettings({ default_language: opt.value })}
                  disabled={saving}
                  className={`px-4 py-4 rounded-xl border-2 font-semibold transition flex flex-col items-center gap-1 ${
                    currentDefault === opt.value
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                  } ${saving ? 'opacity-50' : ''}`}
                  data-testid={`default-lang-${opt.value}`}
                >
                  <span className="text-2xl">{opt.flag}</span>
                  <span className="text-sm">{opt.label}</span>
                  {currentDefault === opt.value && (
                    <span className="text-[10px] uppercase font-bold mt-1 bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                      Əsas
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Auto-translate toggle */}
          <div className="mt-6 flex items-start justify-between p-4 bg-stone-50 rounded-xl border border-stone-200">
            <div className="flex-1 pr-4">
              <h3 className="font-semibold text-stone-900 text-sm">Avtomatik tərcümə</h3>
              <p className="text-xs text-stone-500 mt-1">
                Açıq olduqda: müştəri başqa dil seçəndə sistem AI ilə avtomatik tərcümə edib göstərir.
                Bağlı olduqda: yalnız sizin əl ilə daxil etdiyiniz tərcümələr istifadə olunur.
              </p>
            </div>
            <button
              onClick={() => saveLanguageSettings({ auto_translate: !autoOn })}
              disabled={saving}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition shadow-inner ${
                autoOn ? 'bg-emerald-500' : 'bg-stone-300'
              } ${saving ? 'opacity-50' : ''}`}
              data-testid="auto-translate-toggle"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition ${
                  autoOn ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
              <span className={`absolute text-[9px] font-bold ${autoOn ? 'left-1.5 text-white' : 'right-1.5 text-stone-600'}`}>
                {autoOn ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>

          {autoOn && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2">
              <span>💡</span>
              <span>
                <strong>Necə işləyir:</strong> Siz menyu məhsullarını və kateqoriyalarını yalnız bir dildə (məsələn,
                Azərbaycan) yazırsınız. Müştəri QR menyuda dili dəyişəndə sistem məzmunu avtomatik olaraq həmin dilə
                tərcümə edib göstərir. Tərcümələr cache-lənir — eyni mətn yalnız bir dəfə tərcümə olunur.
              </span>
            </div>
          )}
        </div>

        {/* QR Code */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
          <h2 className="text-xl font-bold text-stone-900 mb-4">📱 QR Kod</h2>
          <div className="flex items-start space-x-6">
            {restaurant.qr_code && (
              <img src={restaurant.qr_code} alt="QR Kod" className="w-48 h-48 border rounded-xl" data-testid="restaurant-qr-code" />
            )}
            <div className="flex-1">
              <p className="text-stone-600 mb-4 text-sm">
                Bu QR kodu skan edərək müştərilər menyunuzu görə bilərlər
              </p>
              <div className="bg-stone-100 p-3 rounded-lg mb-4">
                <p className="text-xs text-stone-700 break-all">{qrMenuUrl}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => window.open(qrMenuUrl, '_blank')}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition text-sm font-medium"
                  data-testid="view-qr-menu-button"
                >
                  Menyuya bax
                </button>
                <button
                  onClick={() => navigate(`/menu/manage/${restaurantId}`)}
                  className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition text-sm font-medium"
                >
                  Menyu İdarəsi
                </button>
                <button
                  onClick={() => navigate(`/restaurant/${restaurantId}/tables`)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition text-sm font-medium"
                  data-testid="tables-management-button"
                >
                  🪑 Masaları idarə et
                </button>
                <button
                  onClick={() => navigate(`/restaurant/${restaurantId}/security`)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-sm font-medium"
                  data-testid="security-button"
                >
                  🛡️ Sifariş qoruması
                </button>
                <button
                  onClick={() => navigate(`/restaurant/${restaurantId}/analytics`)}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition text-sm font-medium"
                  data-testid="analytics-button"
                >
                  📊 Analitika + Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
